import {
  ContainerSortOption,
  ContainerStatus,
} from '@rematter/frontend-lib-api-client';

import { ContainerRecord } from '../types/container';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import {
  maybeParseZonedDateTime,
  parseZonedDateTime,
} from '~/shared/utils/dateTime';

export const containersListQueryAtom = createQueryAtomFactory(
  'trade.containersList',
  (params: {
    ids?: string[];
    bookingIds?: string[];
    containerTypeIds?: string[];
    facilityIds?: string[];
    loadIds?: string[];
    status?: ContainerStatus[];
    containerNumber?: string;
    searchTerm?: string;
    limit?: number;
    offset?: number;
    sorting?: ContainerSortOption[];
  }) => ({
    containers: {
      __args: {
        filter: {
          ids: params.ids,
          bookingIds: params.bookingIds,
          containerTypeIds: params.containerTypeIds,
          facilityIds: params.facilityIds,
          loadIds: params.loadIds,
          status: params.status,
          containerNumber: params.containerNumber,
          searchTerm: params.searchTerm,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 10,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        containerNumber: true,
        sealNumber: true,
        status: true,
        bookingId: true,
        booking: {
          id: true,
          documentNumber: true,
        },
        containerTypeId: true,
        containerType: {
          id: true,
          name: true,
        },
        loadId: true,
        load: {
          id: true,
          documentNumber: true,
        },
        facilityId: true,
        facility: {
          id: true,
          facilityName: true,
        },
        pickedUpAt: true,
        arrivedAtFacilityAt: true,
        loadedAt: true,
        departedFacilityAt: true,
        returnedToPortAt: true,
        shippedAt: true,
        organizationId: true,
        createdBy: true,
        createdByUser: {
          id: true,
          firstName: true,
          lastName: true,
        },
        updatedBy: true,
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): {
      containers: ContainerRecord[];
      totalCount: number;
    } => {
      if (!data || !data.containers || !data.containers.entries) {
        return { containers: [], totalCount: 0 };
      }

      if (!data.containers.entries.length) {
        return { containers: [], totalCount: 0 };
      }

      return {
        containers: data.containers.entries.map((container) => ({
          id: container.id,
          containerNumber: container.containerNumber || null,
          sealNumber: container.sealNumber || null,
          status: container.status as ContainerRecord['status'],
          bookingId: container.bookingId,
          booking: container.booking
            ? {
                id: container.booking.id,
                bookingNumber: container.booking.documentNumber,
              }
            : null,
          containerTypeId: container.containerTypeId,
          containerType: container.containerType || null,
          loadId: container.loadId || null,
          load: container.load
            ? {
                id: container.load.id,
                documentNumber: container.load.documentNumber,
              }
            : null,
          facilityId: container.facilityId || null,
          facility: container.facility || null,
          pickedUpAt: maybeParseZonedDateTime(container.pickedUpAt),
          arrivedAtFacilityAt: maybeParseZonedDateTime(
            container.arrivedAtFacilityAt,
          ),
          loadedAt: maybeParseZonedDateTime(container.loadedAt),
          departedFacilityAt: maybeParseZonedDateTime(
            container.departedFacilityAt,
          ),
          returnedToPortAt: maybeParseZonedDateTime(container.returnedToPortAt),
          shippedAt: maybeParseZonedDateTime(container.shippedAt),
          organizationId: container.organizationId,
          createdBy: container.createdBy,
          createdByUser: container.createdByUser || null,
          updatedBy: container.updatedBy,
          timestamps: {
            createdAt: parseZonedDateTime(container.timestamps.createdAt),
            updatedAt: parseZonedDateTime(container.timestamps.updatedAt),
          },
        })),
        totalCount: data.containers.page?.total || 0,
      };
    },
  },
);
